import { AnyAri } from '@atlassian/ari/any-ari';
import type { AsapClaims } from '@atlassian/asap-interface';
import jwtDecode from 'jwt-decode';

import { aPersonCanUseWhiteboards } from '@canvas/capability-summaries';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';
import { Deferrable } from '@canvas/deferrable';
import { CanvasDocumentId } from '@canvas/document-id';
import { Action, isAction } from '@canvas/model/permissions';
import { CustomerImpact } from '@canvas/sentry-core';
import { captureExceptionForCapability } from '@canvas/sentry-core';

import { TOKEN_FETCH_TIMEOUT_MS } from './constants.ts';
import {
  type DocumentId,
  type PaywallFeaturesSynchronizer,
  type Token,
  type TokenClaimsSynchronizer,
  type TokenClaimsSynchronizerError,
  TokenFetchError,
  TokenIdMismatchError,
  type TokenManager,
  type TokenRefresher,
  type TokenRefresherError,
  type TokenRefresherV2,
} from './types.ts';
import { abortSignalWithTimeout, resetClaims, withAbortSignal } from './utils.ts';

function getIdFromMaybeAri(documentIdOrAri: string | undefined): DocumentId | undefined {
  if (!documentIdOrAri) {
    return;
  }

  try {
    return CanvasDocumentId.parse(documentIdOrAri).documentId as DocumentId;
  } catch (ex: unknown) {
    return documentIdOrAri as DocumentId;
  }
}

export class TokenManagerV2 implements TokenManager {
  private deferrable: Deferrable<TokenRefresherV2>;
  private refresherError: TokenRefresherError | undefined;
  private synchronizer: TokenClaimsSynchronizer | undefined;
  private synchronizerError: TokenClaimsSynchronizerError | undefined;
  private paywallFeaturesSynchronizer: PaywallFeaturesSynchronizer | undefined;

  /**
   * An integer used for tracking the most recent token request
   * @private
   */
  private epoch = 0;

  /**
   * The most recent access token that was processed, used to prevent triggering
   * the synchronizer multiple times for the same token
   * @private
   */
  private previousAccessToken?: string;

  constructor(tokenRefresher?: TokenRefresherV2) {
    if (tokenRefresher) {
      this.deferrable = Deferrable.resolved(tokenRefresher);
    } else {
      this.deferrable = new Deferrable();
    }
  }

  /**
   * Get a token
   * @param documentIdOrAri The document ID or ARI to get the token for
   * @param retriesRemaining The number of retries remaining
   */
  public async token(documentIdOrAri?: string, retriesRemaining = 5): Promise<Token> {
    // Increment the epoch to prevent any in-flight requests from updating claims
    const fetchEpoch = ++this.epoch;

    const token = await this.getTokenWithRetries(documentIdOrAri, retriesRemaining);

    // If this request is still the most recent, update the claims
    if (fetchEpoch === this.epoch) {
      this.synchronizeToken(token);
    }

    return token;
  }

  /**
   * Get a token with a specific identifier. Will throw if the token does not match the identifier.
   * @param documentIdOrAri The document identifier
   * @deprecated Use `token` instead as it can also accept a document id
   * @see TokenManagerV2.token
   */
  async tokenForDocumentIdOrThrow(documentIdOrAri: string): Promise<Token> {
    const token = await this.token(documentIdOrAri);

    const documentId = AnyAri.parse(token.board).resourceId;
    if (documentId !== documentIdOrAri && documentIdOrAri !== token.board) {
      throw new TokenIdMismatchError();
    }

    return token;
  }

  updateToken(token: Token | undefined) {
    // Prevent any in-flight requests from updating claims
    this.epoch++;

    this.synchronizeToken(token);
  }

  private synchronizeToken(token: Token | undefined) {
    this.synchronizeAccessClaims(token);
    this.synchronizePaywallFeatures(token);
  }

  private parseClaims(token: Token): AsapClaims | undefined {
    const decodedToken: AsapClaims = jwtDecode(token.accessToken);
    if (!decodedToken || !('access' in decodedToken)) {
      return;
    }

    return decodedToken.access;
  }

  private synchronizeAccessClaims = (token: Token | undefined) => {
    /**
     * If no one is listening for claims changes, ignore.
     */
    if (!this.synchronizer) {
      return;
    }

    /**
     * If we have already handled this access token we don't need to process
     * it again
     */
    if (this.previousAccessToken === token?.accessToken) {
      return;
    }

    this.previousAccessToken = token?.accessToken;

    const boardId =
      token !== undefined && token.board.length > 0 ? token.board : undefined;

    /**
     * If the token is empty, treat it as a permissions resetting event.
     */
    if (this.isEmpty(token)) {
      this.synchronizer(boardId, resetClaims());
      return;
    }

    try {
      const claims = this.parseClaims(token);
      /**
       * If no claims are provided, treat it as a permissions resetting event.
       */
      if (!claims) {
        this.synchronizer(boardId, resetClaims());
        return;
      }

      let maybeBoardClaims: any[] = claims[token.board] ?? [];

      // If no claims found for the exact ARI, fall back to matching by document
      // UUID. This handles cases where the token's board ARI uses a different
      // resource type (e.g. 'board') than the JWT claims key (e.g. 'slide').
      if (maybeBoardClaims.length === 0) {
        try {
          const tokenDocumentId = AnyAri.parse(token.board).resourceId;
          for (const [claimAri, claimPerms] of Object.entries(claims)) {
            try {
              const claimDocumentId = AnyAri.parse(claimAri).resourceId;
              if (claimDocumentId === tokenDocumentId && Array.isArray(claimPerms)) {
                maybeBoardClaims = claimPerms;
                break;
              }
            } catch {
              // Skip unparseable claim keys
            }
          }
        } catch {
          // token.board is not a valid ARI — no fallback possible
        }
      }

      const boardClaims = new Set<Action>(maybeBoardClaims.filter(isAction));

      const rbacCallsitesEnabled = getExperimentValue(
        'permissionsRbacDependenciesCallsites',
        'isEnabled',
        { fireExperimentExposure: false },
      );

      if (rbacCallsitesEnabled) {
        if (!token.dataSecurityPolicy?.pageExport) {
          boardClaims.delete(Action.export);
        }
      } else if (token.dataSecurityPolicy?.pageExport) {
        boardClaims.add(Action.export);
      }

      if (token.dataSecurityPolicy?.attachmentDownload) {
        boardClaims.add(Action['download/attachment']);
      }

      this.synchronizer(boardId, { board: boardClaims });
    } catch (err) {
      /**
       * In the event of an error, fallback to an empty claim set.
       */
      this.synchronizer(boardId, resetClaims());
      this.synchronizerError?.(err);
    }
  };

  private synchronizePaywallFeatures = (token: Token | undefined) => {
    if (!this.paywallFeaturesSynchronizer) {
      return;
    }

    if (token === undefined) {
      return;
    }

    if (token.features === undefined) {
      return;
    }

    this.paywallFeaturesSynchronizer(token.features);
  };

  /**
   * Refreshes the token by fetching a new token through the provided refresher
   */
  async refreshToken(): Promise<void> {
    if (!this.deferrable.fulfilled) {
      return;
    }

    try {
      await this.token();
    } catch (err) {
      this.refresherError?.(err);
      throw err;
    }
  }

  /**
   * Initializes the first token even if there is no previous token in the manager
   */
  async initializeToken() {
    await this.refreshToken();
  }

  onRefreshToken(handler: TokenRefresher) {
    this.deferrable.resolve(handler);
    this.deferrable = Deferrable.resolved(handler);
  }

  onRefreshTokenErrored(handler: TokenRefresherError) {
    this.refresherError = handler;
  }

  onSynchronizeClaims(handler: TokenClaimsSynchronizer) {
    this.synchronizer = handler;
  }

  onSynchronizeClaimsError(handler: TokenClaimsSynchronizerError) {
    this.synchronizerError = handler;
  }

  onPaywallFeatureFetch(handler: PaywallFeaturesSynchronizer) {
    this.paywallFeaturesSynchronizer = handler;
  }

  isEmpty(token: Token | undefined): token is undefined {
    return !(token && token.accessToken && token.accessToken.length > 0);
  }

  private async getTokenWithRetries(
    documentIdOrAri?: string,
    retriesRemaining = 5,
  ): Promise<Token> {
    try {
      const maybeToken = await this.getTokenWithTimeout(documentIdOrAri);

      if (!this.isEmpty(maybeToken)) {
        return maybeToken;
      }
    } catch (err) {
      console.error('Error getting token', err, retriesRemaining);
    }

    if (retriesRemaining > 1) {
      return this.getTokenWithRetries(documentIdOrAri, retriesRemaining - 1);
    }

    const error = new TokenFetchError('Could not fetch token after multiple attempts');
    this.refresherError?.(error);
    captureExceptionForCapability(
      error,
      {
        tags: {
          captureLocation: 'ShortcutLoader-getTokenWithRetries-failedToFetch',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      },
      aPersonCanUseWhiteboards,
    );
    throw error;
  }

  private async getTokenWithTimeout(documentIdOrAri?: string) {
    const abortOperation = abortSignalWithTimeout(TOKEN_FETCH_TIMEOUT_MS);

    const id = getIdFromMaybeAri(documentIdOrAri);
    const pendingTokenFetch = (async () => await (await this.deferrable)(id))();

    try {
      return await withAbortSignal<Token | undefined>(
        pendingTokenFetch,
        abortOperation.signal,
        `Token fetch timed out after ${TOKEN_FETCH_TIMEOUT_MS}ms`,
      );
    } finally {
      abortOperation.clear();
    }
  }
}

/**
 * The token manager instance.
 * @deprecated Use an instance of the token manager rather than this global.
 */
export const tokenManager: TokenManager = new TokenManagerV2();
